import { AxiosError, AxiosResponse } from "axios";
import dayjs from "dayjs";
import { IAccount, IAccountLite, IBranding, IForm, IValidationResult, forms } from "lobbie";
import ls from "localstorage-slim";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import {
    API_PREFIX,
    BASE_API_URL,
    DEFAULT_ERROR_MESSAGE,
    EUserType,
    LOBBIE_STORAGE,
    ROUTES,
} from "src/constants";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import {
    useReduxActiveFormGroupAccountPatient,
    useReduxActiveFormGroupArchived,
    useReduxActiveFormGroupForms,
    useReduxActiveFormGroupId,
} from "src/hooks/forms/formGroups/useReduxActiveFormGroup";
import { useAxiosPoster } from "src/hooks/useAxios";
import { useThrottleAsyncFunction } from "src/hooks/useThrottle";
import { useLogoutNoRedirect } from "src/hooks/user";
import { setLocation, setLocationId, setLocations, setMultiMultiLocations } from "src/redux";
import { setAccount } from "src/redux/actions/accountActions";
import { setBranding } from "src/redux/actions/brandingActions";
import { setFormStorageKey } from "src/redux/actions/formActions";
import { setActiveFormGroup } from "src/redux/actions/formGroupActions";
import {
    handleError,
    isFailedRequest,
    localSet,
    logDev,
    notify,
    sessionGet,
    sessionRemove,
    setUserStorage,
} from "src/utils";
import { retry } from "src/utils/lazy";
import FullScreenLoadingSpinner from "../../shared/loading/FullScreenLoadingSpinner";
const PatientFormsHeader = lazy(() => retry(() => import("./PatientFormsHeader")));
const PatientFormsList = lazy(() => retry(() => import("./PatientFormsList")));
const PatientFormsPermissionsModal = lazy(() =>
    retry(() => import("./PatientFormsPermissionsModal")),
);
const PatientFormsDOBVerificationModal = lazy(() =>
    retry(() => import("src/components/patients/forms/PatientFormsDOBVerificationModal")),
);

interface IResponseData {
    account: IAccountLite;
    formGroup: forms.IFormGroup;
    branding: IBranding;
    isKeepFormsOpen: boolean;
    isComplete: boolean;
}

const LOGGED_OUT_ACTIONS = {} as Record<string, string>;

const PatientFormsHome: React.FC = () => {
    logDev("PatientFormsHome.init");
    const dispatch = useDispatch();
    const { push, replace } = useHistory();
    const { account } = useLobbieAccountRedux();
    const poster = useAxiosPoster();
    const logout = useLogoutNoRedirect(true);
    const formGroupId = useReduxActiveFormGroupId();
    const formGroupArchived = useReduxActiveFormGroupArchived();
    const formGroupForms = useReduxActiveFormGroupForms();
    const formGroupAccountPatient = useReduxActiveFormGroupAccountPatient();
    const [failureMessage, setFailureMessage] = useState<string>("");

    const actionId: string | null = useMemo(
        () =>
            new URLSearchParams(window.location.search).get("actionId") ||
            new URLSearchParams(window.location.search).get("token"),
        [],
    );
    useEffect(() => {
        if (actionId) {
            const isLoggedOutKey = `logout/${actionId}`;

            logDev("LOGGED_OUT_ACTIONS", LOGGED_OUT_ACTIONS);

            if (!LOGGED_OUT_ACTIONS[actionId] && !localStorage?.getItem(isLoggedOutKey)) {
                logout()
                    .then(() => {
                        LOGGED_OUT_ACTIONS[actionId] = "1";
                        localStorage?.setItem(`logout/${actionId}`, "1");
                    })
                    .catch((e) => {
                        LOGGED_OUT_ACTIONS[actionId] = "1";
                        localStorage?.setItem(`logout/${actionId}`, "1");
                        console.error(e);
                    });
            }
        }
    }, [actionId, logout]);

    useEffect(() => {
        if (formGroupArchived) {
            push(ROUTES.formArchived);
        }
    }, [push, formGroupArchived]);

    const formGroupFormsTodoComplete = useMemo(() => {
        return formGroupForms.reduce(
            (sum, form) => {
                if (form?.formTemplateVersion?.formTemplate?.isStaffOnly) {
                    return sum;
                }

                const key = form.complete ? "complete" : "todo";
                return {
                    ...sum,
                    [key]: sum[key].concat(form),
                };
            },
            { todo: [] as IForm[], complete: [] as IForm[] },
        );
    }, [formGroupForms]);

    const handleResult = useCallback(
        (data: IResponseData) => {
            ls.set(LOBBIE_STORAGE.Local.Patient.Forms.Token, actionId, {
                ttl: Math.abs(dayjs().diff(dayjs().add(1, "hour"), "seconds")),
                encrypt: true,
            });
            if (
                data.isComplete &&
                data.isKeepFormsOpen &&
                sessionGet(LOBBIE_STORAGE.Session.Patients.Forms.FormCompleted) === "1"
            ) {
                sessionRemove(LOBBIE_STORAGE.Session.Patients.Forms.FormCompleted);
                replace(ROUTES.formCompletion);
            } else {
                if (data?.account) {
                    dispatch(setAccount(data.account as IAccount));
                }
                if (data?.branding) {
                    dispatch(setBranding(data.branding));
                }
                if (data?.formGroup) {
                    dispatch(setActiveFormGroup(data.formGroup));
                    if (data.formGroup.location) {
                        dispatch(setLocationId(data.formGroup.location.id));
                        dispatch(setLocation(data.formGroup.location));
                        dispatch(setLocations([data.formGroup.location]));
                        dispatch(setMultiMultiLocations([data.formGroup.location]));
                        localSet(
                            LOBBIE_STORAGE.Local.Locations.Location.Name,
                            data.formGroup.location.name,
                        );
                    }
                    if (data.formGroup.accountPatient) {
                        if (data.formGroup.accountPatient.patientId) {
                            setUserStorage(
                                data.formGroup.accountPatient.patientId,
                                EUserType.Patient,
                            );
                        }
                        dispatch(setFormStorageKey(data.formGroup.accountPatient.id.toString()));
                    }
                }
            }
        },
        [actionId, dispatch, replace],
    );

    const getForms = useCallback(async () => {
        if (!actionId) return;

        poster(`${BASE_API_URL}/${API_PREFIX}/action/forms`, { actionId })
            .then((response: AxiosResponse | void) => {
                const result = response && (response.data as IResponseData | IValidationResult);
                if (!result) {
                    response?.status &&
                        response.status > 399 &&
                        notify({
                            level: "warning",
                            title: "Found no assigned forms.",
                            message: DEFAULT_ERROR_MESSAGE,
                        });
                    setFailureMessage(DEFAULT_ERROR_MESSAGE);
                } else if (isFailedRequest(result)) {
                    const message = (result as IValidationResult)?.message;
                    if (message === "complete") {
                        replace(ROUTES.formCompletion);
                    } else {
                        notify({
                            level: "warning",
                            title: "Found no assigned forms.",
                            message: message || DEFAULT_ERROR_MESSAGE,
                        });
                        setFailureMessage(message || DEFAULT_ERROR_MESSAGE);
                    }
                } else {
                    handleResult(result as IResponseData);
                }
            })
            .catch((error: AxiosError) => {
                handleError(error, {
                    title: "Error getting forms.",
                });
                setFailureMessage(DEFAULT_ERROR_MESSAGE);
            });
    }, [actionId, poster, replace, handleResult]);

    const getForms_Throttled = useThrottleAsyncFunction(getForms, 1000);
    useEffect(() => {
        getForms_Throttled().catch(console.error);
    }, [getForms_Throttled]);

    const handleUpdatePermissions = useCallback(
        (agreed: boolean) => {
            if (!formGroupAccountPatient) return;
            dispatch(
                setActiveFormGroup({
                    accountPatient: {
                        ...formGroupAccountPatient,
                        isMinimumAgeConfirmedAccount: agreed,
                    },
                }),
            );
        },
        [dispatch, formGroupAccountPatient],
    );

    if (!actionId) {
        return null;
    }

    if (
        account?.verifyDOBOnFormsOpen &&
        formGroupId &&
        formGroupAccountPatient &&
        !formGroupAccountPatient?.isDOBVerified
    ) {
        return (
            <Suspense fallback={<FullScreenLoadingSpinner message="Loading..." />}>
                <PatientFormsDOBVerificationModal
                    account={account}
                    formGroupId={formGroupId}
                    formGroupAccountPatient={formGroupAccountPatient}
                />
            </Suspense>
        );
    } else if (account?.minimumAgeYears && !formGroupAccountPatient?.isMinimumAgeConfirmedAccount) {
        return (
            <Suspense fallback={<FullScreenLoadingSpinner message="Loading..." />}>
                <PatientFormsPermissionsModal
                    account={account}
                    isMinimumAgeConfirmedAccount={
                        formGroupAccountPatient?.isMinimumAgeConfirmedAccount
                    }
                    handleUpdate={handleUpdatePermissions}
                />
            </Suspense>
        );
    }

    if (failureMessage) {
        return <div className="container-fluid body-large text-center mt-5">{failureMessage}</div>;
    }

    if (!account) {
        return <FullScreenLoadingSpinner message={["Loading Forms..."]} />;
    }

    if (!formGroupAccountPatient) {
        return <FullScreenLoadingSpinner message={[`Loading ${account.patientFieldName}...`]} />;
    }

    return (
        <Suspense
            fallback={
                <FullScreenLoadingSpinner
                    message={`Loading Forms${formGroupAccountPatient?.name ? " for " + formGroupAccountPatient.name : ""}...`}
                />
            }
        >
            <PatientFormsHeader />
            {account.publicUuid && (
                <PatientFormsList
                    accountPublicUuid={account.publicUuid}
                    accountPatient={formGroupAccountPatient}
                    account={account}
                    dueDate={null}
                    todo={formGroupFormsTodoComplete?.todo || []}
                    completed={formGroupFormsTodoComplete?.complete || []}
                />
            )}
        </Suspense>
    );
};

export default PatientFormsHome;
